/**
 * locale.ts — språkvalg for offentlige sider (forsiden)
 *
 * Valget lagres i localStorage. Uten lagret valg følges nettleserens språk
 * (norsk bokmål/nynorsk → «no», alt annet → «en»). Samme store-mønster som
 * lib/theme.ts, slik at useSyncExternalStore gir hydreringssikker oppførsel.
 */

import type { Locale } from '@/types/landing';

export const LOCALE_STORAGE_KEY = 'insuite-lang';

/** Språket serveren rendrer (og klienten før hydrering). */
export const DEFAULT_LOCALE: Locale = 'no';

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function fromBrowser(): Locale {
  const lang = (navigator.language || '').toLowerCase();
  return lang.startsWith('nb') || lang.startsWith('nn') || lang.startsWith('no') ? 'no' : 'en';
}

export function readLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === 'no' || stored === 'en') return stored;
  } catch {
    // lagring er valgfritt
  }
  return fromBrowser();
}

export function setLocale(locale: Locale) {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // lagring er valgfritt
  }
  emit();
}

export function subscribeLocale(onChange: () => void) {
  const onStorage = (e: StorageEvent) => {
    if (e.key === LOCALE_STORAGE_KEY || e.key === null) onChange();
  };
  listeners.add(onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onStorage);
  };
}
